import { useEffect, useRef, useState } from "react"
import { Gauge } from "lucide-react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"
import { MAX_SPEED, MIN_SPEED } from "./timeline-model"

const PRESETS = [0.5, 1, 1.5, 2]

type Props = {
  /** Текущая скорость выделенного клипа; null — клип не выбран. */
  speed: number | null
  onChange: (speed: number) => void
}

/** Кнопка скорости в тулбаре: поповер со слайдером, числовым полем и пресетами. */
export function ClipSpeedControl({ speed, onChange }: Props) {
  const { t } = useTranslation(["shot"])
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (speed == null) setOpen(false)
  }, [speed])

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("pointerdown", onDown)
    return () => document.removeEventListener("pointerdown", onDown)
  }, [open])

  const value = speed ?? 1
  const isActive = speed != null && speed !== 1

  return (
    <div ref={rootRef} className="relative inline-flex leading-none">
      <ActionHintTooltip label={t("timeline.speed")}>
        <button
          type="button"
          disabled={speed == null}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "rounded p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-40",
            open || isActive
              ? "bg-ui-surface-raised text-ui-accent-text"
              : "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary",
          )}
        >
          <Gauge className="h-4 w-4" />
        </button>
      </ActionHintTooltip>
      {open && speed != null ? (
        <div className="absolute right-0 bottom-full z-40 mb-1 w-56 rounded-lg border border-ui-border p-2.5 shadow-xl dark:bg-zinc-900 light:bg-ui-bg-elevated">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-medium text-ui-text-secondary">{t("timeline.speed")}</span>
            <input
              type="number"
              min={MIN_SPEED}
              max={MAX_SPEED}
              step={0.01}
              value={value}
              onChange={(e) => {
                const next = Number(e.target.value)
                if (Number.isFinite(next) && next > 0) onChange(next)
              }}
              className="h-6 w-16 rounded border border-ui-border bg-ui-surface-sunken px-1 text-right text-[11px] text-ui-text-primary tabular-nums"
            />
          </div>
          <input
            type="range"
            min={MIN_SPEED}
            max={MAX_SPEED}
            step={0.01}
            value={value}
            aria-label={t("timeline.speed")}
            onChange={(e) => onChange(Number(e.target.value))}
            className="mt-2 h-1 w-full cursor-pointer accent-[var(--ui-accent)]"
          />
          <div className="mt-2 flex items-center gap-1">
            {PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => onChange(preset)}
                className={cn(
                  "flex-1 rounded px-1 py-0.5 text-[10px] tabular-nums transition-colors",
                  value === preset
                    ? "bg-ui-surface-raised text-ui-accent-text"
                    : "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary",
                )}
              >
                {preset}x
              </button>
            ))}
          </div>
          <p className="mt-2 text-[9px] leading-snug text-ui-text-muted">{t("timeline.speedHint")}</p>
        </div>
      ) : null}
    </div>
  )
}
